// src/components/customers/QuickAddJobModal.jsx
import { useState, useEffect } from "react";

/**
 * QuickAddJobModal Component
 * A responsive modal for quickly adding jobs with plan, products, and address fields
 * Features enhanced UI/UX with custom icons, animations, and mobile responsiveness
 */
const QuickAddJobModal = ({
  isOpen,
  onClose,
  onSave,
  customerAddress = {},
}) => {
  // Form state with all required fields
  const [formData, setFormData] = useState({
    plan: "",
    products: "",
    address: "",
    postcode: "",
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Animation state for smooth transitions
  const [isAnimating, setIsAnimating] = useState(false);

  // Set default address from customer when modal opens
  useEffect(() => {
    if (isOpen && customerAddress) {
      setFormData((prev) => ({
        ...prev,
        address: customerAddress.address || "",
        postcode: customerAddress.postcode || "",
      }));
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, customerAddress]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.plan.trim()) {
      newErrors.plan = "Plan is required";
    }

    if (!formData.products.trim()) {
      newErrors.products = "Products are required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.postcode.trim()) {
      newErrors.postcode = "Postcode is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSave(formData);
      // Reset form after successful save
      setFormData({
        plan: "",
        products: "",
        address: customerAddress.address || "",
        postcode: customerAddress.postcode || "",
      });
      setErrors({});
    }
  };

  // Handle modal close
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
      // Reset form and errors
      setFormData({
        plan: "",
        products: "",
        address: "",
        postcode: "",
      });
      setErrors({});
    }, 150);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop with fade animation */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isAnimating ? "bg-opacity-50" : "bg-opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Modal container - centered and responsive */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all duration-300 ${
            isAnimating ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
        >
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-t-xl">
            <h3 className="text-2xl font-bold flex items-center gap-2">
              <span className="text-3xl">💼</span>
              Quick Add Job
            </h3>
            <p className="text-green-100 mt-1">Fill in the job details below</p>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Plan Field */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <span className="text-green-500">📋</span>
                Job Plan
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="plan"
                value={formData.plan}
                onChange={handleInputChange}
                placeholder="Describe the job plan..."
                rows="3"
                className={`w-full px-4 py-3 border rounded-lg resize-none transition-all duration-200 
                  focus:ring-2 focus:ring-green-500 focus:border-transparent
                  ${errors.plan ? "border-red-500" : "border-gray-300"}
                  hover:border-gray-400`}
              />
              {errors.plan && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />
                  {errors.plan}
                </p>
              )}
            </div>

            {/* Products Field */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <span className="text-green-500">📦</span>
                Products Needed
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="products"
                value={formData.products}
                onChange={handleInputChange}
                placeholder="List the products needed..."
                rows="3"
                className={`w-full px-4 py-3 border rounded-lg resize-none transition-all duration-200 
                  focus:ring-2 focus:ring-green-500 focus:border-transparent
                  ${errors.products ? "border-red-500" : "border-gray-300"}
                  hover:border-gray-400`}
              />
              {errors.products && (
                <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                  <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />
                  {errors.products}
                </p>
              )}
            </div>

            {/* Address Fields Container */}
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <span className="text-green-500">📍</span>
                Job Location
              </h4>

              {/* Address Field */}
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter job address..."
                  className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 
                    focus:ring-2 focus:ring-green-500 focus:border-transparent
                    ${errors.address ? "border-red-500" : "border-gray-300"}
                    hover:border-gray-400`}
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />
                    {errors.address}
                  </p>
                )}
              </div>

              {/* Postcode Field */}
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  Postcode <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    #
                  </span>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    placeholder="Enter postcode..."
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all duration-200 
                      focus:ring-2 focus:ring-green-500 focus:border-transparent
                      ${errors.postcode ? "border-red-500" : "border-gray-300"}
                      hover:border-gray-400`}
                  />
                </div>
                {errors.postcode && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full" />
                    {errors.postcode}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium
                  hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 
                  focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 
                  text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 
                  transition-all duration-200 transform hover:scale-[1.02] focus:outline-none 
                  focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Add Job
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickAddJobModal;
